import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  MotionProvider,
  Reveal,
  StarField,
  Meteors,
  FloatingOrb,
} from "../Components/motion/space-primitives";
import { listArticles } from "../lib/articles";
import Footer from "../Components/rebrand/Footer";

// Shared cosmic backdrop (drifting starfield + meteors) used behind the header.
const SpaceBackdrop = () => (
  <>
    <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
      <div
        className="bg-drift absolute inset-y-0 left-0 flex h-full"
        style={{ width: "200%", animationDuration: "90s" }}
      >
        <div className="relative h-full w-1/2 shrink-0">
          <StarField count={70} />
        </div>
        <div className="relative h-full w-1/2 shrink-0">
          <StarField count={70} />
        </div>
      </div>
    </div>
    <Meteors count={4} className="z-0 opacity-70" />
    <FloatingOrb size={460} color="rgba(90, 169, 230, 0.16)" style={{ top: -140, right: "6%" }} />
    <FloatingOrb size={360} color="rgba(153, 223, 175, 0.14)" style={{ top: 120, left: "-6%" }} />
  </>
);

const TagList = ({ tags, accent }) => (
  <div className="flex flex-wrap gap-2">
    {(tags || []).map((tag) => (
      <span
        key={tag}
        className="rounded-full px-3 py-1 text-xs font-semibold"
        style={{ color: accent, backgroundColor: `${accent}1A` }}
      >
        {tag}
      </span>
    ))}
  </div>
);

const Cover = ({ post, className }) => {
  const theme = post.theme;
  return (
    <div className={`relative flex items-center justify-center overflow-hidden ${className}`}>
      {post.coverImageUrl ? (
        <img
          src={post.coverImageUrl}
          alt={post.title}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      ) : (
        <div className="absolute inset-0" style={{ background: theme?.coverGradient }} />
      )}
      {theme?.logo && (
        <img
          src={theme.logo}
          alt={`${post.title} logo`}
          className="relative h-14 w-auto opacity-90 transition-opacity group-hover:opacity-100"
        />
      )}
    </div>
  );
};

const ArticleCard = ({ post }) => {
  const accent = post.theme?.accent || "#99DFAF";
  return (
    <Link
      to={`/articles/${post.slug}`}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] no-underline transition-all hover:-translate-y-1 hover:border-primary/30"
    >
      <Cover post={post} className="h-44" />
      <div className="flex flex-1 flex-col gap-3 p-6">
        <TagList tags={post.tags?.slice(0, 2)} accent={accent} />
        <h3 className="text-xl font-bold leading-snug text-white transition-colors group-hover:text-primary">
          {post.title}
        </h3>
        <p className="flex-1 text-sm text-gray-400">{post.excerpt}</p>
        <div className="flex items-center justify-between pt-2 text-xs text-gray-500">
          <span>{post.date}</span>
          <span>{post.readingTime}</span>
        </div>
      </div>
    </Link>
  );
};

const Articles = () => {
  const [articles, setArticles] = useState(undefined);

  useEffect(() => {
    listArticles()
      .then(setArticles)
      .catch(() => setArticles([]));
  }, []);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Articles | Nodefleet";
    return () => {
      document.title = prevTitle;
    };
  }, []);

  return (
    <MotionProvider>
      <div className="relative -mt-24 min-h-screen bg-space">
        {/* Header */}
        <section className="relative flex min-h-[60vh] w-full items-center overflow-hidden bg-space-deep py-32 text-white max-sm:py-28">
          <SpaceBackdrop />
          <div className="relative z-10 w-full px-8 max-sm:px-6 lg:px-20">
            <Reveal>
              <span className="inline-block rounded-full border border-primary/40 px-4 py-1 text-sm text-primary">
                Nodefleet Articles
              </span>
            </Reveal>
            <Reveal
              as="h1"
              delay={0.08}
              className="mt-6 max-w-4xl text-6xl font-extrabold leading-[1.02] tracking-tight max-sm:text-5xl sm:text-7xl"
            >
              Product updates
              <br />
              <span className="text-brand-gradient italic">& engineering notes</span>
            </Reveal>
            <Reveal delay={0.16} className="mt-6 max-w-xl text-lg text-white/60 max-sm:text-base">
              What we've shipped and learned building infrastructure for Web3.
            </Reveal>
          </div>
        </section>

        {/* Grid */}
        <section className="w-full px-8 py-16 max-sm:px-6 lg:px-20">
          {articles === undefined && (
            <p className="text-center text-gray-500">Loading articles…</p>
          )}
          {articles?.length === 0 && (
            <p className="text-center text-gray-500">No articles published yet.</p>
          )}
          {articles && articles.length > 0 && (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {articles.map((post) => (
                <ArticleCard key={post.slug} post={post} />
              ))}
            </div>
          )}
        </section>

        <Footer />
      </div>
    </MotionProvider>
  );
};

export default Articles;
